import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { useMemo } from 'react';

import useTypedParams from '../hooks/useTypedParams';
import { API } from './api';

export interface APIClient {
  request: AxiosInstance['request'];

  get<T = unknown, R = AxiosResponse<T>, D = unknown, URL extends API['url'] = string>(
    url: URL,
    config?: Extract<API, { method: 'GET'; url: URL }> extends {
      params: infer APIP;
      data: infer APID;
    }
      ? Omit<AxiosRequestConfig<APID>, 'params'> & { params?: APIP }
      : AxiosRequestConfig<D>
  ): Promise<
    Extract<API, { method: 'GET'; url: URL }> extends { response: infer APIR }
      ? AxiosResponse<APIR>
      : R
  >;

  delete<T = unknown, R = AxiosResponse<T>, D = unknown, URL extends API['url'] = string>(
    url: URL,
    config?: Extract<API, { method: 'DELETE'; url: URL }> extends {
      params: infer APIP;
      data: infer APID;
    }
      ? Omit<AxiosRequestConfig<APID>, 'params'> & { params?: APIP }
      : AxiosRequestConfig<D>
  ): Promise<
    Extract<API, { method: 'DELETE'; url: URL }> extends { response: infer APIR }
      ? AxiosResponse<APIR>
      : R
  >;

  head<T = unknown, R = AxiosResponse<T>, D = unknown, URL extends API['url'] = string>(
    url: URL,
    config?: Extract<API, { method: 'HEAD'; url: URL }> extends {
      params: infer APIP;
      data: infer APID;
    }
      ? Omit<AxiosRequestConfig<APID>, 'params'> & { params?: APIP }
      : AxiosRequestConfig<D>
  ): Promise<
    Extract<API, { method: 'HEAD'; url: URL }> extends { response: infer APIR }
      ? AxiosResponse<APIR>
      : R
  >;

  options<T = unknown, R = AxiosResponse<T>, D = unknown, URL extends API['url'] = string>(
    url: URL,
    config?: Extract<API, { method: 'OPTIONS'; url: URL }> extends {
      params: infer APIP;
      data: infer APID;
    }
      ? Omit<AxiosRequestConfig<APID>, 'params'> & { params?: APIP }
      : AxiosRequestConfig<D>
  ): Promise<
    Extract<API, { method: 'OPTIONS'; url: URL }> extends { response: infer APIR }
      ? AxiosResponse<APIR>
      : R
  >;

  post<T = void, R = AxiosResponse<T>, D = unknown, URL extends API['url'] = string>(
    url: URL,
    data?: Extract<API, { method: 'POST'; url: URL }> extends { data: infer APID } ? APID : D,
    config?: Extract<API, { method: 'POST'; url: URL }> extends {
      params: infer APIP;
      data: infer APID;
    }
      ? Omit<AxiosRequestConfig<APID>, 'params'> & { params?: APIP }
      : AxiosRequestConfig<D>
  ): Promise<
    Extract<API, { method: 'POST'; url: URL }> extends { response: infer APIR }
      ? AxiosResponse<APIR>
      : R
  >;

  put<T = unknown, R = AxiosResponse<T>, D = unknown, URL extends API['url'] = string>(
    url: URL,
    data?: Extract<API, { method: 'PUT'; url: URL }> extends { data: infer APID } ? APID : never,
    config?: Extract<API, { method: 'PUT'; url: URL }> extends {
      params: infer APIP;
      data: infer APID;
    }
      ? Omit<AxiosRequestConfig<APID>, 'params'> & { params?: APIP }
      : AxiosRequestConfig<D>
  ): Promise<
    Extract<API, { method: 'PUT'; url: URL }> extends { response: infer APIR }
      ? AxiosResponse<APIR>
      : R
  >;

  patch<T = unknown, R = AxiosResponse<T>, D = unknown, URL extends API['url'] = string>(
    url: URL,
    data?: Extract<API, { method: 'PATCH'; url: URL }> extends { data: infer APID } ? APID : never,
    config?: Extract<API, { method: 'PATCH'; url: URL }> extends {
      params: infer APIP;
      data: infer APID;
    }
      ? Omit<AxiosRequestConfig<APID>, 'params'> & { params?: APIP }
      : AxiosRequestConfig<D>
  ): Promise<
    Extract<API, { method: 'PATCH'; url: URL }> extends { response: infer APIR }
      ? AxiosResponse<APIR>
      : R
  >;
}

const createAPIClient = (tenant: string) => {
  return axios.create({
    timeout: 100000,
    baseURL: `/${tenant}/api/v1`,
    headers: {
      'X-CSRFToken': Cookies.get('csrftoken') || '',
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  }) as APIClient;
};

const useAPIClient = () => {
  const { tenant } = useTypedParams({ tenant: String });
  const api = useMemo<APIClient>(() => createAPIClient(tenant), [tenant]);

  return api;
};

export default useAPIClient;
